import { createColumnHelper } from '@tanstack/react-table';

import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import { isCurrencyISO } from '@/constants/currencies';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';

import { HeaderCell } from './HeaderCell';
import { Tooltip } from '../Tooltip';

const columnHelper =
  createColumnHelper<PortfolioDetailedReturns['holdings'][number]>();

export const getTableOverviewColumns = (portfolioCurrencyISO?: unknown) => {
  const ISO = isCurrencyISO(portfolioCurrencyISO)
    ? portfolioCurrencyISO
    : 'XXX';
  return [
    columnHelper.accessor(
      ({ company }) => {
        const { name, uniqueSymbol } = company;
        return {
          name,
          uniqueSymbol,
        };
      },
      {
        header: () => <HeaderCell label="Ticker" className="w-x15" />,
        id: 'ticker',
        cell: (cell) => {
          const { name, uniqueSymbol } = cell.getValue();
          return (
            <div className="grid w-x15 grid-rows-[auto,auto] gap-x0">
              <span className="truncate">{name}</span>
              <span className="text-tiny text-soft">{uniqueSymbol}</span>
            </div>
          );
        },
      }
    ),
    columnHelper.accessor('detailedReturns.units', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Shares"
              tip="The number of shares held at the end of the selected period."
            />
          }
        />
      ),
      cell: (cell) => {
        return cell.getValue();
      },
    }),
    columnHelper.accessor(
      ({ company, detailedReturns }) => {
        const { price } = detailedReturns;
        const { data } = company;
        return {
          data,
          price,
        };
      },
      {
        header: () => (
          <HeaderCell
            label={
              <Tooltip
                label="Price"
                tip="The stock's last closing price on the end date of the selected period."
              />
            }
          />
        ),
        id: 'price',
        cell: (cell) => {
          const { data, price } = cell.getValue();
          const currencyISO =
            getTradingItemCurrencyISO(data.currencyInfo) ?? 'XXX';
          return formatterCurrency(price, currencyISO);
        },
      }
    ),
    columnHelper.accessor('detailedReturns.value', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Value"
              tip="The market value of shares held at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.costBasis', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Cost Basis"
              tip="The total purchase cost of shares held at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.unrealizedGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Unrealized"
              tip="The gain/loss on unsold shares based on their value at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.realizedGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Realized"
              tip="The gain/loss from shares sold during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.dividends', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Dividends"
              tip="Total dividends received during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.currencyGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Currency Gains"
              tip="Foreign exchange gain/loss from shares sold during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.totalGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Total Gains"
              tip="The overall gain/loss, including realised, unrealised, dividends, and currency effects, in the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Annualized (IRR)"
              tip="The annualised return rate for the selected period, calculated using the Internal Rate of Return (IRR)."
            />
          }
        />
      ),
      accessorKey: 'detailedReturns.annualisedReturn',
      cell: (cell) => {
        const value = cell.getValue();
        if (value === null) {
          return (
            <Tooltip
              label="No data"
              tip="To calculate IRR, the position must be held for at least 90 days, and the selected period must also be 90 days or more."
            />
          );
        }
        return formatterPercentage(value);
      },
    },
  ];
};

export const getTableOverviewColumnsWithNoDataTooltips = (
  portfolioCurrencyISO: unknown
) => {
  const ISO = isCurrencyISO(portfolioCurrencyISO)
    ? portfolioCurrencyISO
    : 'XXX';
  return [
    columnHelper.accessor(
      ({ company }) => {
        const { name, uniqueSymbol } = company;
        return {
          name,
          uniqueSymbol,
        };
      },
      {
        header: () => <HeaderCell label="Ticker" className="w-x15" />,
        id: 'ticker',
        cell: (cell) => {
          const { name, uniqueSymbol } = cell.getValue();
          return (
            <div className="grid w-x15 grid-rows-[auto,auto] gap-x0">
              <span className="truncate">{name}</span>
              <span className="text-tiny text-soft">{uniqueSymbol}</span>
            </div>
          );
        },
      }
    ),
    columnHelper.accessor('detailedReturns.units', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Shares"
              tip="The number of shares held at the end of the selected period."
            />
          }
        />
      ),
      cell: (cell) => {
        return cell.getValue();
      },
    }),
    columnHelper.accessor(
      ({ company, detailedReturns }) => {
        const { price } = detailedReturns;
        const { data } = company;
        return {
          data,
          price,
        };
      },
      {
        header: () => (
          <HeaderCell
            label={
              <Tooltip
                label="Price"
                tip="The stock's last closing price on the end date of the selected period."
              />
            }
          />
        ),
        id: 'price',
        cell: (cell) => {
          const { data, price } = cell.getValue();
          const currencyISO =
            getTradingItemCurrencyISO(data.currencyInfo) ?? 'XXX';
          return formatterCurrency(price, currencyISO);
        },
      }
    ),
    columnHelper.accessor('detailedReturns.value', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Value"
              tip="The market value of shares held at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.costBasis', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Cost Basis"
              tip="The total purchase cost of shares held at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.unrealizedGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Unrealized"
              tip="The gain/loss on unsold shares based on their value at the end of the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    columnHelper.accessor('detailedReturns.realizedGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Realized"
              tip="The gain/loss from shares sold during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: () => {
        return (
          <Tooltip
            label="No data"
            tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
          />
        );
      },
    }),
    columnHelper.accessor('detailedReturns.dividends', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Dividends"
              tip="Total dividends received during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: () => {
        return (
          <Tooltip
            label="No data"
            tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
          />
        );
      },
    }),
    columnHelper.accessor('detailedReturns.currencyGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Currency Gains"
              tip="Foreign exchange gain/loss from shares sold during the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: () => {
        return (
          <Tooltip
            label="No data"
            tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
          />
        );
      },
    }),
    columnHelper.accessor('detailedReturns.totalGains', {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Total Gains"
              tip="The overall gain/loss, including realised, unrealised, dividends, and currency effects, in the selected period."
            />
          }
          currencyISO={ISO}
        />
      ),
      cell: (cell) => {
        return formatterCurrency(cell.getValue(), 'XXX');
      },
    }),
    {
      header: () => (
        <HeaderCell
          label={
            <Tooltip
              label="Annualized (IRR)"
              tip="The annualised return rate for the selected period, calculated using the Internal Rate of Return (IRR)."
            />
          }
        />
      ),
      accessorKey: 'detailedReturns.annualisedReturn',
      cell: () => {
        return (
          <Tooltip
            label="No data"
            tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
          />
        );
      },
    },
  ];
};

export const TABLE_OVERVIEW_COLUMN_LENGTH = getTableOverviewColumns().length;
